import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Pill, Stethoscope, Briefcase, Sparkles, ClipboardList, Download, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { exportKioskReportPdf } from "../lib/pdfExport";

const TAB_KEYS = ["vitals", "prescriptions", "notes", "visits", "employer", "ai"];
const TAB_ICONS = { vitals: ClipboardList, prescriptions: Pill, notes: Stethoscope, visits: Stethoscope, employer: Briefcase, ai: Sparkles };

export function MedicalRecords({ patient } = {}) {
    const { t } = useTranslation();
    const [tab, setTab] = useState("vitals");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [zoomImg, setZoomImg] = useState(null);
    const [downloadingIdx, setDownloadingIdx] = useState(null);

    const handleDownloadPdf = async (vitals, idx) => {
        try {
            setDownloadingIdx(idx);
            await exportKioskReportPdf(vitals, patient || {});
            toast.success("Report downloaded.");
        } catch (e) {
            toast.error("Could not generate PDF: " + (e?.message || "unknown"));
        } finally {
            setDownloadingIdx(null);
        }
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get("/me/medical-records");
                if (mounted) setData(data);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Could not load medical records");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    if (loading) return <div className="py-16 text-center text-gray-500">{t("common.loading")}</div>;

    const counts = data ? {
        vitals: data.vitals_history.length,
        prescriptions: data.prescriptions.length,
        notes: data.doctor_notes.length,
        visits: data.visits.length,
        employer: data.employer_remarks.length,
        ai: data.ai_reports.length,
    } : {};

    const empty = (k) => (
        <div className="card-soft p-10 text-center text-gray-500" data-testid={`mr-empty-${k}`}>
            <FileText className="w-8 h-8 mx-auto text-[var(--medx-orange)] mb-3" />
            {t(`dashboard.medicalRecords.empty.${k}`)}
        </div>
    );

    return (
        <div className="space-y-6" data-testid="tab-medical-records">
            <div>
                <h2 className="font-display text-2xl font-semibold">{t("dashboard.medicalRecords.title")}</h2>
                <p className="text-sm text-gray-600 max-w-2xl mt-1">{t("dashboard.medicalRecords.subtitle")}</p>
            </div>

            <div className="flex flex-wrap gap-2" data-testid="mr-tabs">
                {TAB_KEYS.map((k) => {
                    const Icon = TAB_ICONS[k];
                    const active = tab === k;
                    return (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            data-testid={`mr-tab-${k}`}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                                active ? "bg-[var(--medx-red)] text-white border-[var(--medx-red)] shadow-md" : "bg-white text-gray-700 border-gray-200 hover:border-[var(--medx-orange)]"
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            {t(`dashboard.medicalRecords.tabs.${k}`)}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${active ? "bg-white/20" : "bg-gray-100 text-gray-500"}`}>
                                {counts[k] ?? 0}
                            </span>
                        </button>
                    );
                })}
            </div>

            {tab === "vitals" && (
                data.vitals_history.length === 0 ? empty("vitals") : (
                    <div className="card-soft overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                                <tr>
                                    <th className="text-left p-4">DATE</th>
                                    <th className="text-left p-4">BP</th>
                                    <th className="text-left p-4">HR</th>
                                    <th className="text-left p-4">SpO₂</th>
                                    <th className="text-left p-4">TEMP</th>
                                    <th className="text-left p-4">SCORE</th>
                                    <th className="text-right p-4">PDF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.vitals_history.map((h, i) => (
                                    <tr key={i} className="border-t border-gray-100 hover:bg-orange-50/40" data-testid={`mr-vital-row-${i}`}>
                                        <td className="p-4 text-gray-700">{new Date(h.captured_at).toLocaleString()}</td>
                                        <td className="p-4">{h.blood_pressure_systolic}/{h.blood_pressure_diastolic}</td>
                                        <td className="p-4">{h.heart_rate}</td>
                                        <td className="p-4">{h.spo2}%</td>
                                        <td className="p-4">{h.body_temperature}°C</td>
                                        <td className="p-4 font-bold text-[var(--medx-red)]">{h.health_score}</td>
                                        <td className="p-4 text-right">
                                            <button
                                                onClick={() => handleDownloadPdf(h, i)}
                                                disabled={downloadingIdx === i}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[var(--medx-orange)]/40 text-[var(--medx-red)] bg-white hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                data-testid={`mr-vital-download-pdf-${i}`}
                                                title="Download this scan as PDF"
                                            >
                                                <Download className="w-3.5 h-3.5" />
                                                {downloadingIdx === i ? "…" : "PDF"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {tab === "prescriptions" && (
                data.prescriptions.length === 0 ? empty("prescriptions") : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.prescriptions.map((rx) => (
                            <div key={rx.id} className="card-soft p-4" data-testid={`mr-prescription-${rx.id}`}>
                                <button onClick={() => setZoomImg(rx)} className="block w-full">
                                    <img src={rx.image_url} alt={rx.label} className="w-full h-44 object-cover rounded-lg bg-gray-100" />
                                </button>
                                <div className="mt-3 flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-medium text-sm text-gray-900 truncate">{rx.label}</div>
                                        <div className="text-xs text-gray-500">{rx.doctor_name}</div>
                                        <div className="text-xs text-gray-400">{new Date(rx.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <a href={rx.image_url} download={`prescription-${rx.id}.png`} className="p-2 rounded-lg hover:bg-orange-50 text-[var(--medx-red)]" title="Download">
                                        <Download className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}

            {tab === "notes" && (
                data.doctor_notes.length === 0 ? empty("notes") : (
                    <div className="space-y-3">
                        {data.doctor_notes.map((n) => (
                            <div key={n.id} className="card-soft p-5" data-testid={`mr-note-${n.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-sm font-semibold text-gray-900">{n.doctor_name}</div>
                                    <div className="text-xs text-gray-400">{new Date(n.created_at).toLocaleString()}</div>
                                </div>
                                <div className="text-sm text-gray-700 whitespace-pre-wrap">{n.note}</div>
                                {n.recommendation && <div className="mt-2 text-xs text-[var(--medx-red)]"><strong>Recommendation:</strong> {n.recommendation}</div>}
                                {n.suggest_appointment_at && <div className="mt-1 text-xs text-gray-500">Suggested follow-up: {new Date(n.suggest_appointment_at).toLocaleString()}</div>}
                            </div>
                        ))}
                    </div>
                )
            )}

            {tab === "visits" && (
                data.visits.length === 0 ? empty("visits") : (
                    <div className="card-soft overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                                <tr>
                                    <th className="text-left p-4">DATE</th>
                                    <th className="text-left p-4">DOCTOR</th>
                                    <th className="text-left p-4">SPECIALITY</th>
                                    <th className="text-left p-4">REASON</th>
                                    <th className="text-left p-4">STATUS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.visits.map((v) => (
                                    <tr key={v.id} className="border-t border-gray-100 hover:bg-orange-50/40" data-testid={`mr-visit-${v.id}`}>
                                        <td className="p-4 text-gray-700">{new Date(v.created_at).toLocaleDateString()}</td>
                                        <td className="p-4">{v.doctor_name}</td>
                                        <td className="p-4 text-gray-500">{v.doctor_speciality || "—"}</td>
                                        <td className="p-4">{v.problem || "—"}</td>
                                        <td className="p-4"><span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${v.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{v.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {tab === "employer" && (
                data.employer_remarks.length === 0 ? empty("employer") : (
                    <div className="space-y-3">
                        {data.employer_remarks.map((r) => (
                            <div key={r.id} className="card-soft p-5" data-testid={`mr-employer-${r.id}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-sm font-semibold text-gray-900">{r.employer_name}</div>
                                    <div className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString()}</div>
                                </div>
                                <div className="text-sm text-gray-700">{r.remark}</div>
                                <div className="mt-1 text-xs text-gray-500">Added by {r.added_by}</div>
                            </div>
                        ))}
                    </div>
                )
            )}

            {tab === "ai" && (
                data.ai_reports.length === 0 ? empty("ai") : (
                    <div className="space-y-4">
                        {data.ai_reports.map((r) => (
                            <div key={r.id} className="card-soft p-5" data-testid={`mr-ai-${r.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs font-mono tracking-wider text-[var(--medx-orange)]">AI VALUATION · {new Date(r.created_at).toLocaleString()}</div>
                                </div>
                                <div className="text-sm text-gray-800 mb-2"><strong>Summary:</strong> {r.report.summary}</div>
                                {r.report.flags?.length > 0 && (
                                    <div className="mb-2">
                                        <div className="text-xs font-semibold text-gray-500 mb-1">{t("dashboard.ai.flags")}</div>
                                        <ul className="space-y-1">
                                            {r.report.flags.map((f, idx) => (
                                                <li key={idx} className="text-xs flex gap-2">
                                                    <span className={`px-2 py-0.5 rounded-full font-mono ${f.severity === "high" ? "bg-red-100 text-red-700" : f.severity === "moderate" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{f.severity}</span>
                                                    <span><strong>{f.vital}</strong> — {f.finding}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3 mt-3">
                                    ⚠ {r.report.disclaimer}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}

            {zoomImg && (
                <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" onClick={() => setZoomImg(null)} data-testid="rx-zoom-modal">
                    <button className="absolute top-4 right-4 text-white p-2" onClick={() => setZoomImg(null)}><X className="w-6 h-6" /></button>
                    <img src={zoomImg.image_url} alt={zoomImg.label} className="max-h-[90vh] max-w-full rounded-xl shadow-2xl" />
                </div>
            )}
        </div>
    );
}
